import { spacing } from '@scality/core-ui';
import styled from 'styled-components';

export const NodeBox = styled.div<{ $color?: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  /* No colour means the root: the frame the boxes are measured against, in the plain border. */
  border: 1px solid ${({ $color, theme }) => $color ?? theme.border};
  border-radius: ${spacing.f8};
  padding: ${spacing.f4};
  min-width: 0;
  overflow: hidden;

  /* Each box tinted with its own colour — color-mix takes the palette's hex as it is, so there is
     no parsing and no need for the theme's hand-picked *RGB companions. */
  background: ${({ $color }) => ($color ? `color-mix(in srgb, ${$color} 15%, transparent)` : 'transparent')};

  &:focus-visible {
    outline: ${spacing.f2} solid ${({ theme }) => theme.selectedActive};
    outline-offset: ${spacing.f1};
  }
`;

/**
 * Holds the label, and the Tooltip wrapper around it.
 *
 * The tooltip belongs to the label, not to the box: `Tooltip` opens on pointer-enter, which reaches
 * every ancestor of the pointer, and a box *contains* its children — so a tooltip there stacks one
 * overlay per level on a single hover. Labels never nest, so exactly one can be under the pointer.
 */
export const LabelSlot = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  /* Tooltip wraps its child in a shrink-to-fit inline-block; let it fill the slot so the label
     inside still has a width to ellipsise against. */
  > * {
    display: block;
    min-width: 0;
    max-width: 100%;
  }
`;

export const LabelText = styled.div`
  padding: 0 ${spacing.f4};

  /* On the text itself, so the ellipsis takes its colour and size. */
  > * {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Room for a letter or two only: the share alone reads better, and the tooltip names the box. */
  @container responsive (max-width: 40px) {
    display: none;
  }
`;

export const NodeValue = styled.div`
  padding: 0 ${spacing.f4};
  overflow: hidden;
  white-space: nowrap;
  /* Digits of equal width: a value ticking 9% → 10% must not shift the boxes beside it. */
  font-variant-numeric: tabular-nums;
`;

/** Lets the button, and the Tooltip wrapper around it, take the rest of the box's height. */
export const FoldedChildrenSlot = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;

  > * {
    display: block;
    flex: 1;
  }
`;

/** Dashed: stands in for the children a folded box hides, and zooms into them. */
export const FoldedChildrenButton = styled.button<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.f4};
  width: 100%;
  height: 100%;
  min-height: ${spacing.f24};
  padding: 0;
  white-space: nowrap;
  border: 1px dashed ${({ $color, theme }) => $color ?? theme.border};
  border-radius: ${spacing.f4};
  background: transparent;
  color: ${({ theme }) => theme.textSecondary};
  cursor: zoom-in;

  &:hover {
    background: ${({ $color }) => ($color ? `color-mix(in srgb, ${$color} 25%, transparent)` : 'transparent')};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    outline: ${spacing.f2} solid ${({ theme }) => theme.selectedActive};
    outline-offset: ${spacing.f1};
  }
`;
